import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Timeframe } from '@pipyar/core';
import { api } from '@/lib/api';

export interface TakenKnot {
  level: number;
  direction: 'up' | 'down' | 'flat';
  formedAt: number;
  /** True UTC instant of the close that took it. */
  takenAt: number;
  close: number;
  from: 'above' | 'below';
  /** Direction of the candle that closed on the knot. */
  candle: 'up' | 'down' | 'flat';
  /** The inverse of that candle's colour — see the core documentation. */
  bias: 'bullish' | 'bearish' | 'neutral';
  barsLater: number;
}

/** A close sitting on a knot, waiting for the next candle to confirm it. */
export interface PendingKnot {
  level: number;
  formedAt: number;
  /** True UTC instant of the candle that closed on the knot. */
  pendingAt: number;
  close: number;
  candle: 'up' | 'down';
  /** The colour the next candle must close for the take to count. */
  needs: 'up' | 'down';
}

export interface KnotRow {
  timeframe: Timeframe;
  total: number;
  live: number;
  knot: TakenKnot | null;
  pending: PendingKnot | null;
}

export interface KnotSummaryResponse {
  symbol: string;
  digits: number;
  /** Null when each timeframe used its own default. */
  tolerance: number | null;
  serverTime: number;
  timeframes: KnotRow[];
}

/**
/**
 * Rows that have a taken knot, newest first.
 *
 * M1 used to be filtered out here — it was permanently the freshest row and
 * fired the alert almost continuously, which trains you to ignore it. The
 * module no longer reads M1 at all (see KNOT_TIMEFRAMES), so the endpoint does
 * not report it and there is nothing left to hide.
 */
export function visibleRows(
  data: KnotSummaryResponse | undefined,
): (KnotRow & { knot: TakenKnot })[] {
  return (data?.timeframes ?? [])
    .filter((r): r is KnotRow & { knot: TakenKnot } => r.knot !== null)
    .sort((a, b) => b.knot.takenAt - a.knot.takenAt);
}

/**
 * Shared by the dashboard tile and the alert.
 *
 * One query key means one request and one cache entry however many components
 * are mounted — the alert lives in the app shell, so on the dashboard both are
 * on screen at once.
 */
export function useKnotSummary(symbol: string | undefined) {
  return useQuery({
    queryKey: ['knots-summary', symbol],
    queryFn: () =>
      api.get<KnotSummaryResponse>(
        `/api/market/knots/summary?symbol=${encodeURIComponent(symbol!)}`,
      ),
    enabled: Boolean(symbol),
    // The dashboard polls fast: these summaries are cheap (tens of
    // milliseconds server-side) and a board you glance at should not be showing
    // a price from a minute ago. React Query pauses the timer while the tab is
    // in the background, so this costs nothing when you are not looking.
    staleTime: 4_000,
    refetchInterval: 5_000,
    // Back on the tab, ask at once: the cached answer is from before it was hidden.
    refetchOnWindowFocus: true,
  });
}

/** Older than this, the summary no longer describes the market. */
const TRUST_MS = 20_000;

/**
 * The server's clock, advanced locally between answers, and whether the last
 * answer is recent enough to act on.
 *
 * A card judged only when a response lands freezes when responses stop — a
 * hidden tab, a sleeping phone, a dropped connection — and then outlives the
 * knot it describes. Ticking here lets a card expire on time regardless, and
 * `trusted` takes it down when the data is too old to say anything.
 */
export function useSummaryClock(query: ReturnType<typeof useKnotSummary>): { nowUtc: number; trusted: boolean } {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const age = nowMs - query.dataUpdatedAt;
  const serverTime = query.data?.serverTime ?? nowMs / 1000;
  return {
    nowUtc: serverTime + Math.max(0, age) / 1000,
    trusted: query.data !== undefined && !query.isError && age < TRUST_MS,
  };
}
